import React from 'react';

import {
  ActionIcon,
  Button,
  Paper,
  Skeleton,
  Stack,
  Text,
  Title,
  Tooltip,
  createStyles,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconCircleX, IconEdit, IconPlus, IconPrinter, IconReport, IconTrashX } from '@tabler/icons-react';
import PayrollPeriodSelect from 'components/form-fields/select-fields/PayrollPeriodSelect';
import Menu from 'components/Menu';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useCreatePayrollPeriod from 'hooks/payrollPeriod/useCreatePayrollPeriod';
import usePayrollPeriod from 'hooks/payrollPeriod/usePayrollPeriod';
import useUpdatePayrollPeriod from 'hooks/payrollPeriod/useUpdatePayrollPeriod';

import ModalBulkPayrollExport from './ModalBulkPayrollExport';
import ModalGeneratePayrollPeriod from './ModalGeneratePayrollPeriod';
import ModalOpenClosePayrollPeriod from './ModalOpenClosePayrollPeriod';
import ModalPayrollPeriodCreate from './ModalPayrollPeriodCreate';
import ModalPayrollPeriodDelete from './ModalPayrollPeriodDelete';
import ModalPayrollPeriodEdit from './ModalPayrollPeriodEdit';

const useStyles = createStyles(theme => ({
  header: {
    background: theme.colors.gray[0],
    padding: theme.spacing.xl,
    paddingBottom: theme.spacing.xl,
    display: 'grid',
    borderBottom: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: 0,
    gridTemplateColumns: 'min-content max-content max-content auto max-content min-content min-content',
    gridTemplateRows: 'auto',
    gridTemplateAreas: `"title total info . period download actions"`,
    alignItems: 'center',
    gap: theme.spacing.md,
    [theme.fn.smallerThan('lg')]: {
      gridTemplateColumns: 'min-content min-content auto min-content min-content min-content',
      gridTemplateAreas: `
        'title title . . download actions'
        'total info . period period period'
        `,
    },
    [theme.fn.smallerThan('md')]: {
      gridTemplateColumns: 'min-content min-content auto min-content min-content',
      gridTemplateAreas: `
        'title title . download actions'
        'total total info info info'
        'period period period period period'
        `,
    },
  },
  title: {
    gridArea: 'title',
    whiteSpace: 'nowrap',
  },
  total: {
    gridArea: 'total',
    whiteSpace: 'nowrap',
  },
  info: {
    gridArea: 'info',
    whiteSpace: 'nowrap',
  },
  period: {
    gridArea: 'period',
    minWidth: '20rem',
  },
  download: {
    gridArea: 'download',
  },
  actions: {
    gridArea: 'actions',
  },
}));

const CREATE_LABEL = 'Create New Payroll Period';

const PayrollReportPageHeader = ({
  permission = true,
  payrollPeriod,
  setPayrollPeriod,
}: {
  permission?: boolean;
  payrollPeriod: string | null;
  setPayrollPeriod: React.Dispatch<React.SetStateAction<string | null>>;
}): JSX.Element => {
  const { classes, theme } = useStyles();
  const { formatCurrency } = useLocalization();

  const { data, isSuccess, isLoading, isError } = usePayrollPeriod(payrollPeriod);

  const updateMutation = useUpdatePayrollPeriod();
  const createMutation = useCreatePayrollPeriod();

  const [
    isEditPayrollPeriodModalOpened,
    { open: openEditPayrollPeriodModal, close: closeEditPayrollPeriodModal },
  ] = useDisclosure(false);
  const [
    isDeletePayrollPeriodModalOpened,
    { open: openDeletePayrollPeriodModal, close: closeDeletePayrollPeriodModal },
  ] = useDisclosure(false);
  const [
    isGeneratePayrollPeriodModalOpened,
    { open: openGeneratePayrollPeriodModal, close: closeGeneratePayrollPeriodModal },
  ] = useDisclosure(false);
  const [
    isClosePayrollPeriodModalOpened,
    { open: openClosePayrollPeriodModal, close: closeClosePayrollPeriodModal },
  ] = useDisclosure(false);
  const [
    isCreatePayrollPeriodModalOpened,
    { open: openCreatePayrollPeriodModal, close: closeCreatePayrollPeriodModal },
  ] = useDisclosure(false);
  const [isBulkExportModalOpened, { open: openBulkExportModal, close: closeBulkExportModal }] =
    useDisclosure(false);

  const handleCreate = (): undefined => {
    openCreatePayrollPeriodModal();
  };

  const getPayrollSummary = (): JSX.Element => {
    if (isLoading) {
      return (
        <div className={classes.total}>
          <Skeleton height='2rem' width='5rem' />
        </div>
      );
    }
    if (isError) {
      return (
        <div className={classes.total}>
          <Title order={1}>Error</Title>
        </div>
      );
    }
    if (isSuccess && data.data) {
      return (
        <>
          <div className={classes.total}>
            <Text color='dimmed' fw='bold' size='1.5rem'>
              {`Total Payout: ${formatCurrency(data.data.payrollPeriodPayout / 100)}`}
            </Text>
          </div>
          <div className={classes.info}>
            <Stack spacing={0}>
              <Text color='dimmed' size='sm'>
                Updated {dayjs(data.data.updatedAt).format('MMMM D YYYY, h:mmA')}
              </Text>
              {data.data.isClosed && (
                <Text color='dimmed' size='sm'>
                  This payroll period is closed.
                </Text>
              )}
            </Stack>
          </div>
        </>
      );
    }
    return <></>;
  };

  return (
    <>
      <Paper className={classes.header}>
        <Title order={1} className={classes.title} pr='2rem'>
          Payroll Report
        </Title>
        {getPayrollSummary()}

        <PayrollPeriodSelect
          data-cy='payrollPeriodSelect'
          className={classes.period}
          value={payrollPeriod}
          onChange={setPayrollPeriod}
          disabled={updateMutation.isLoading || createMutation.isLoading}
        />

        <Tooltip label='Export payroll reports' className={classes.download}>
          <ActionIcon onClick={openBulkExportModal} data-cy='bulkPayrollExportButton'>
            <IconPrinter />
          </ActionIcon>
        </Tooltip>

        <div className={classes.actions}>
          <Menu
            position='bottom-end'
            items={[
              {
                title: CREATE_LABEL,
                'data-cy': 'createPayrollPeriod',
                onClick: handleCreate,
                icon: <IconPlus size={theme.fontSizes.sm} />,
                disabled: !permission,
              },
              {
                title: 'Generate Report',
                'data-cy': 'generatePayrollReportButton',
                onClick: openGeneratePayrollPeriodModal,
                icon: <IconReport size={theme.fontSizes.sm} />,
                disabled: !permission || !data || data.data.isClosed,
              },
              {
                title: data?.data.isClosed ? 'Reopen Report' : 'Close Report',
                'data-cy': 'closePayrollReportButton',
                onClick: openClosePayrollPeriodModal,
                icon: <IconCircleX size={theme.fontSizes.sm} />,
                disabled: !permission || !data,
              },
              {
                title: 'Edit',
                'data-cy': 'editPayrollReportButton',
                onClick: openEditPayrollPeriodModal,
                icon: <IconEdit size={theme.fontSizes.sm} />,
                disabled: !permission || !data || data.data.isClosed,
              },
              {
                title: 'Delete',
                'data-cy': 'deletePayrollReportButton',
                color: 'red',
                icon: <IconTrashX size={theme.fontSizes.sm} />,
                onClick: openDeletePayrollPeriodModal,
                disabled: !permission || !data || data.data.isClosed,
              },
            ]}
            target={
              <Button loading={updateMutation.isLoading || isLoading} data-cy='actionsPayrollReportButton'>
                Actions
              </Button>
            }
          />
        </div>
      </Paper>

      <ModalPayrollPeriodCreate
        opened={isCreatePayrollPeriodModalOpened}
        closeHandler={closeCreatePayrollPeriodModal}
        handlePayrollPeriodChange={setPayrollPeriod}
        createMutation={createMutation}
      />
      <ModalGeneratePayrollPeriod
        opened={isGeneratePayrollPeriodModalOpened}
        handleClose={closeGeneratePayrollPeriodModal}
        payrollPeriod={data?.data}
        updateMutation={updateMutation}
      />
      <ModalOpenClosePayrollPeriod
        opened={isClosePayrollPeriodModalOpened}
        handleClose={closeClosePayrollPeriodModal}
        payrollPeriod={data?.data}
        updateMutation={updateMutation}
      />
      <ModalPayrollPeriodEdit
        opened={isEditPayrollPeriodModalOpened}
        handleClose={closeEditPayrollPeriodModal}
        payrollPeriod={data?.data}
        updateMutation={updateMutation}
      />
      <ModalPayrollPeriodDelete
        opened={isDeletePayrollPeriodModalOpened}
        handleClose={closeDeletePayrollPeriodModal}
        clearPayrollPeriod={() => setPayrollPeriod(null)}
        payrollPeriodId={data?.data.payrollPeriodId}
      />
      <ModalBulkPayrollExport opened={isBulkExportModalOpened} handleClose={closeBulkExportModal} />
    </>
  );
};

export default PayrollReportPageHeader;
